/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import _ from 'lodash';
import { decorate, observable, runInAction, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Container, Icon } from 'semantic-ui-react';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import {
  isStoreLoading,
  isStoreEmpty,
  isStoreNotEmpty,
  isStoreError,
  isStoreReady,
} from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

import CatalogueHeader from './CatalogueHeader';
import CatalogueFilterButtons from './CatalogueFilterButtons';
import CatalogueProductCard from './CatalogueProductCard';
import CatalogueViewToggle from './CatalogueViewToggle';
import Paginate from '../helpers/Paginate';
import { catalogueFilterNames, productMatchesFilter } from './catalogueFilterUtils';
import { createCatalogueCounts } from './catalogueCountUtils';

// expected props
// - catalogueProductsStore (via injection)
// - envTypesStore (via injection)
// - scEnvironmentsStore (via injection)
// - CatalogueView (via injection)
class CatalogueProductsList extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.search = '';
      this.statusFilter = catalogueFilterNames.ALL;
      this.page = 1;
      this.perPage = 50;
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const store = this.store;
    swallowError(store.load());
    swallowError(this.props.envTypesStore.load());
    // Counts are derived from the environments already in this store. BaseStore.load()
    // returns the in-flight promise when one exists, so this does not duplicate a load
    // the workspaces page may have started. No heartbeat is started or stopped here:
    // this is an admin configuration screen, not a live workspace monitor.
    swallowError(this.props.scEnvironmentsStore.load());
  }

  get store() {
    return this.props.catalogueProductsStore;
  }

  get viewStore() {
    return this.props.CatalogueView;
  }

  get compactMode() {
    return this.viewStore.compactMode;
  }

  get counts() {
    return createCatalogueCounts(this.props.scEnvironmentsStore.list);
  }

  // An error is treated as not-ready even when the store holds data: ScEnvironmentsStore
  // pages through results and mutates its map incrementally, so a mid-loop failure leaves
  // a partially populated list. A placeholder is honest there; half a count is not.
  get countsReady() {
    const envsStore = this.props.scEnvironmentsStore;
    return isStoreReady(envsStore) && !isStoreError(envsStore);
  }

  handleCompactToggle = () => {
    // Only the view preference changes. Search, filter, page and perPage are untouched.
    this.viewStore.toggleCompactMode();
  };

  handleSearchChange = search => {
    runInAction(() => {
      this.search = search;
      this.page = 1;
    });
  };

  handlePageChange = number => {
    runInAction(() => {
      this.page = number;
      window.scrollTo(0, 0);
    });
  };

  handlePerPageChange = number => {
    runInAction(() => {
      this.page = 1;
      this.perPage = number;
    });
  };

  handleStatusFilterChange = status => {
    runInAction(() => {
      this.statusFilter = status;
      this.page = 1;
    });
  };

  getFilteredProducts() {
    const store = this.store;
    const envTypesStore = this.props.envTypesStore;
    let products = store.list;

    // Apply status filter
    if (this.statusFilter && this.statusFilter !== catalogueFilterNames.ALL) {
      products = _.filter(products, product => productMatchesFilter(this.statusFilter, product, envTypesStore));
    }

    // Apply search filter
    if (this.search) {
      const searchString = `(${_.escapeRegExp(this.search)})`;
      const exp = new RegExp(searchString, 'i');
      products = _.filter(products, product => exp.test(product.productName) || exp.test(product.description));
    }

    return products;
  }

  getPaginatedProducts() {
    const filtered = this.getFilteredProducts();
    const firstIndex = (this.page - 1) * this.perPage;
    const lastIndex = Math.min(this.page * this.perPage, filtered.length);
    return {
      paginatedList: filtered.slice(firstIndex, lastIndex),
      filteredCount: filtered.length,
    };
  }

  render() {
    const store = this.store;
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} onRetry={() => store.load()} />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder segmentCount={3} />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else if (isStoreNotEmpty(store)) {
      content = this.renderMain();
    }

    return (
      <Container className="mt3 animated fadeIn">
        {content}
      </Container>
    );
  }

  renderEmpty() {
    return (
      <div className="workspace-empty-state">
        <div className="workspace-empty-state-icon workspace-empty-state-icon-primary">
          <Icon name="box" />
        </div>
        <div className="workspace-empty-state-title">No products found</div>
        <div className="workspace-empty-state-subtitle">
          There are no products available in the catalogue.
        </div>
      </div>
    );
  }

  renderMain() {
    const { paginatedList, filteredCount } = this.getPaginatedProducts();
    const isEmpty = _.isEmpty(paginatedList);
    const compactMode = this.compactMode;
    const counts = this.counts;
    const countsReady = this.countsReady;

    return (
      <div data-testid="catalogue-products">
        <CatalogueHeader
          onSearchChange={this.handleSearchChange}
          viewControl={<CatalogueViewToggle compactMode={compactMode} onCompactToggle={this.handleCompactToggle} />}
        >
          <CatalogueFilterButtons
            selectedFilter={this.statusFilter}
            onSelectedFilter={this.handleStatusFilterChange}
            filteredCount={filteredCount}
          />
        </CatalogueHeader>
        <Paginate
          entriesPerPage={this.perPage}
          totalEntries={filteredCount}
          currentPage={this.page}
          onPageChange={this.handlePageChange}
          onPerPageChange={this.handlePerPageChange}
        >
          {/* The wrapper and the per-card div are rendered in both views, with only their
              className changing. Cards hold their expanded state locally, so any change to
              the element tree shape here would remount them and collapse them all
              (Requirement 3.4). */}
          <div className={compactMode ? 'workspace-grid-compact' : 'catalogue-list-stack'}>
            {_.map(paginatedList, product => (
              <div key={product.productId} className={compactMode ? '' : 'mb3'}>
                <CatalogueProductCard
                  product={product}
                  compactMode={compactMode}
                  counts={counts}
                  countsReady={countsReady}
                />
              </div>
            ))}
          </div>
          {isEmpty && (
            <div className="workspace-empty-state">
              <div className="workspace-empty-state-icon">
                <Icon name="search" />
              </div>
              <div className="workspace-empty-state-title">No products match your search</div>
              <div className="workspace-empty-state-subtitle">
                Try adjusting your search criteria.
              </div>
            </div>
          )}
        </Paginate>
      </div>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(CatalogueProductsList, {
  store: computed,
  viewStore: computed,
  compactMode: computed,
  counts: computed,
  countsReady: computed,
  search: observable,
  statusFilter: observable,
  page: observable,
  perPage: observable,
  handleSearchChange: action,
  handleStatusFilterChange: action,
  handlePageChange: action,
  handlePerPageChange: action,
  handleCompactToggle: action,
});

export default inject(
  'catalogueProductsStore',
  'envTypesStore',
  'scEnvironmentsStore',
  'CatalogueView',
)(observer(CatalogueProductsList));
